/**
 * Agent-view resolution for the Support Tasks page.
 *
 * Extracted from SupportTasks.tsx so the role/selection gating is unit-testable
 * without rendering the full page. It decides three things from the current
 * dropdown selection and the viewer's role:
 *   - `selectedAgent`      which agent's tasks the list scopes to (null = none)
 *   - `unassignedOnly`     whether the "Unassigned" (agent_id IS NULL) view is active
 *   - `tasksQueryEnabled`  whether the task-list query should fire
 *
 * Behaviour contract:
 *   - The "__unassigned__" sentinel selects the Unassigned triage bucket for BOTH
 *     admins and support agents. It is agent-independent, so `selectedAgent` is
 *     null and the query runs the unassigned path.
 *   - A support agent otherwise DEFAULTS to (and is locked to) their own profile —
 *     they never see the cross-agent list, so a support agent can only ever scope
 *     to "my tasks" or "unassigned", never another agent.
 *   - Admin behaviour is unchanged: no selection = All Agents (null), a specific
 *     id = that agent, the sentinel = unassigned.
 */
import type { SupportAgent } from "../types/supportTasks.types";

// Distinct filter mode the agent dropdown can toggle into; treated separately
// from a real agent id selection.
export const UNASSIGNED_FILTER_SENTINEL = "__unassigned__";

// "Escalated to me" (2026-10-03): tasks whose escalated_to is the viewer's own ACTIVE
// support_agents row. Offered to any role that has one (support agents and admins alike).
export const ESCALATED_FILTER_SENTINEL = "__escalated__";

export interface AgentViewInput {
  selectedAgentId: string | null;
  isSupportAgent: boolean;
  isSupportAdmin: boolean;
  currentAgentProfile: SupportAgent | null | undefined;
  agents: SupportAgent[];
}

export interface AgentView {
  selectedAgent: SupportAgent | null;
  unassignedOnly: boolean;
  /** The viewer's agent id when the "Escalated to me" view is active, else null. */
  escalatedTo: string | null;
  tasksQueryEnabled: boolean;
}

/** True when this viewer can use "Escalated to me": they have an ACTIVE support_agents row. */
export function canViewEscalatedToMe(
  currentAgentProfile: SupportAgent | null | undefined,
): boolean {
  return !!currentAgentProfile?.id && currentAgentProfile.is_active === true;
}

export function resolveAgentView(input: AgentViewInput): AgentView {
  const {
    selectedAgentId,
    isSupportAgent,
    isSupportAdmin,
    currentAgentProfile,
    agents,
  } = input;

  const unassignedOnly = selectedAgentId === UNASSIGNED_FILTER_SENTINEL;
  const escalatedTo =
    selectedAgentId === ESCALATED_FILTER_SENTINEL &&
    canViewEscalatedToMe(currentAgentProfile)
      ? currentAgentProfile!.id
      : null;

  let selectedAgent: SupportAgent | null;
  if (escalatedTo) {
    // Escalated-to-me is agent-independent: the tasks belong to other agents.
    selectedAgent = null;
  } else if (unassignedOnly) {
    // Unassigned view is agent-independent for every role.
    selectedAgent = null;
  } else if (isSupportAgent && currentAgentProfile) {
    // Support agents are locked to their own profile (never the cross-agent list).
    selectedAgent = currentAgentProfile;
  } else if (!selectedAgentId) {
    // Admin "All Agents".
    selectedAgent = null;
  } else {
    selectedAgent = agents.find((a) => a.id === selectedAgentId) ?? null;
  }

  // Admin: always enabled (All Agents when null). Any role: enabled once an
  // agent is selected. Support agent viewing Unassigned: enabled even though
  // selectedAgent is null, so the unassigned path can fetch.
  const tasksQueryEnabled =
    isSupportAdmin ||
    !!selectedAgent ||
    (isSupportAgent && unassignedOnly) ||
    !!escalatedTo;

  return { selectedAgent, unassignedOnly, escalatedTo, tasksQueryEnabled };
}

/**
 * Where a deep-linked task (/support-tasks?task=<id>) should open: which tab, and which scope
 * the agent selector should switch to (undefined = leave the selector alone).
 *
 * 2026-10-03: the panel opened and immediately closed, because switching tab/scope fired a
 * "clear the selected task" effect AFTER the task was selected. The page now clears the
 * selection only on USER-driven tab/scope changes; this helper picks a view that actually
 * contains the task, so the list and the open panel agree:
 *   - escalated to the viewer (and not their own task) -> "Escalated to me"
 *   - admin and the task has an agent                  -> that agent
 *   - unassigned and open                              -> the Unassigned tab
 *   - cancelled has no tab                              -> Completed (closest closed bucket)
 */
export function resolveDeepLinkView(
  task: {
    status: string;
    agent_id?: string | null;
    escalated_to?: string | null;
  },
  viewer: {
    isSupportAdmin: boolean;
    currentAgentProfile: SupportAgent | null | undefined;
  },
): {
  tab: "pending" | "in_progress" | "completed" | "unassigned";
  agentSelection?: string | null;
} {
  const open = task.status === "pending" || task.status === "in_progress";
  const statusTab: "pending" | "in_progress" | "completed" =
    task.status === "pending" || task.status === "in_progress"
      ? task.status
      : "completed";
  const me = viewer.currentAgentProfile;
  if (
    task.escalated_to &&
    canViewEscalatedToMe(me) &&
    task.escalated_to === me!.id &&
    task.agent_id !== me!.id
  ) {
    return { tab: statusTab, agentSelection: ESCALATED_FILTER_SENTINEL };
  }
  if (!task.agent_id && open) {
    return { tab: "unassigned" };
  }
  if (viewer.isSupportAdmin && task.agent_id) {
    return { tab: statusTab, agentSelection: task.agent_id };
  }
  return { tab: statusTab };
}
